import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Dimensions from "react-native-web/dist/exports/Dimensions";
import Platform from "react-native-web/dist/exports/Platform";
import Pressable from "react-native-web/dist/exports/Pressable";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Text from "react-native-web/dist/exports/Text";
import View from "react-native-web/dist/exports/View";
import propTypes from './shared';
import { applyShadowToken, applyTextStyles, useThemeTokens } from '../ThemeProvider';
import { a11yProps, selectSystemProps, selectTokens, viewProps } from '../utils';
import Backdrop from './Backdrop';
import getTooltipPosition from './getTooltipPosition';
import TooltipButton from '../TooltipButton';
import useCopy from '../utils/useCopy';
import dictionary from './dictionary';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);

const selectTooltipStyles = _ref => {
  let {
    backgroundColor,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    borderRadius
  } = _ref;
  return {
    backgroundColor,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    borderRadius
  };
};

const selectTooltipShadowStyles = _ref2 => {
  let {
    shadow,
    borderRadius
  } = _ref2;
  return {
    borderRadius,
    ...applyShadowToken(shadow)
  };
};

const selectTooltipPositionStyles = _ref3 => {
  let {
    top,
    left,
    width
  } = _ref3;
  return {
    top,
    left,
    width
  };
};

const selectArrowStyles = (_ref4, _ref5) => {
  let {
    backgroundColor,
    arrowWidth,
    arrowBorderRadius,
    shadow
  } = _ref4;
  let {
    position,
    width: tooltipWidth,
    height: tooltipHeight
  } = _ref5;
  // the arrow width is actually a diagonal of the rectangle that we'll use as a tip
  const rectangleSide = Math.sqrt(arrowWidth * arrowWidth / 2); // position the arrow at the side and center of the tooltip - this happens before rotation
  // so we use the rectangle size as basis

  const verticalOffset = -1 * rectangleSide / 2;
  const horizontalOffset = rectangleSide / 2; // percentage-based absolute positioning doesn't act well on native, so we have to
  // calculate the pixel values

  const directionalStyles = {
    above: {
      bottom: verticalOffset,
      left: tooltipWidth / 2 - horizontalOffset,
      transform: [{
        rotateZ: '45deg'
      }]
    },
    below: {
      top: verticalOffset,
      left: tooltipWidth / 2 - horizontalOffset,
      transform: [{
        rotateZ: '-135deg'
      }]
    },
    left: {
      right: verticalOffset,
      top: tooltipHeight / 2 - horizontalOffset,
      transform: [{
        rotateZ: '-45deg'
      }]
    },
    right: {
      left: verticalOffset,
      top: tooltipHeight / 2 - horizontalOffset,
      transform: [{
        rotateZ: '135deg'
      }]
    }
  };
  return {
    backgroundColor,
    width: rectangleSide,
    height: rectangleSide,
    borderBottomRightRadius: arrowBorderRadius,
    // this corner will be the arrow tip after rotation
    ...applyShadowToken(shadow),
    ...directionalStyles[position]
  };
};

const selectTextStyles = tokens => applyTextStyles(selectTokens('Typography', tokens));

const defaultControl = (pressableState, variant) => /*#__PURE__*/_jsx(TooltipButton, {
  pressableState: pressableState,
  variant: variant
});
/**
 * Tooltip provides a descriptive and detailed explanation or instructions. It can be used next to an input label
 * to help a user fill it in, or as a standalone component.
 *
 * By default the TooltipButton component will be used as a control for triggering the tooltip, but you may attach
 * a tooltip to any other component. A render function can be used to adjust the control's styling on state changes (hover, focus, etc.).
 *
 * ### Positioning
 * By default a Tooltip will be automatically positioned in a way that ensures it fits within the viewport.
 * You may suggest a position with a prop - it will be used, unless the tooltip would end up outside the viewport.
 *
 * ### Usage criteria
 * - You may use one when the information is useful only to a small percentage of users (ie. tech savvy people wouldn't need this info).
 * - Tooltips may also be useful when vertical space is an issue.
 */


const Tooltip = /*#__PURE__*/forwardRef((_ref6, ref) => {
  let {
    children,
    content,
    position = 'auto',
    copy = 'en',
    tokens,
    variant,
    ...rest
  } = _ref6;
  const [isOpen, setIsOpen] = useState(false);
  const controlRef = useRef();
  const [controlLayout, setControlLayout] = useState(null);
  const [tooltipDimensions, setTooltipDimensions] = useState(null);
  const [windowDimensions, setWindowDimensions] = useState(Dimensions.get('window'));
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const getCopy = useCopy({
    dictionary,
    copy
  });
  const themeTokens = useThemeTokens('Tooltip', tokens, variant);
  const {
    arrowWidth,
    arrowOffset
  } = themeTokens;
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', _ref7 => {
      let {
        window
      } = _ref7;
      setWindowDimensions(window);
    });
    return () => subscription === null || subscription === void 0 ? void 0 : subscription.remove();
  });

  const toggleIsOpen = () => setIsOpen(!isOpen);

  const close = () => setIsOpen(false);

  const getPressableState = _ref8 => {
    let {
      pressed,
      hovered,
      focused
    } = _ref8;
    return {
      pressed,
      hover: hovered,
      focus: focused
    };
  };

  const onTooltipLayout = _ref9 => {
    let {
      nativeEvent: {
        layout: {
          width,
          height
        }
      }
    } = _ref9;

    if (tooltipDimensions === null || tooltipDimensions.width !== width || tooltipDimensions.height !== height) {
      setTooltipDimensions({
        width: Platform.select({
          web: width + 0.3,
          // avoids often unnecessary line breaks due to subpixel rendering of fonts
          native: width
        }),
        height
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      controlRef.current.measureInWindow((x, y, width, height) => {
        setControlLayout({
          x,
          y,
          width,
          height
        });
      });
    } else {
      setControlLayout(null);
      setTooltipDimensions(null);
      setTooltipPosition(null);
    }
  }, [isOpen]);
  useEffect(() => {
    setIsOpen(false);
  }, [windowDimensions]);
  useEffect(() => {
    if (tooltipPosition !== null && !(tooltipPosition !== null && tooltipPosition !== void 0 && tooltipPosition.isNormalized) || !isOpen || controlLayout === null || tooltipDimensions == null) {
      return;
    }

    const updatedPosition = getTooltipPosition(position, {
      controlLayout,
      tooltipDimensions,
      windowDimensions,
      arrowWidth,
      arrowOffset
    }); // avoid ending up in an infinite normalization loop

    if (tooltipPosition !== null && tooltipPosition !== void 0 && tooltipPosition.isNormalized && updatedPosition.isNormalized) {
      return;
    }

    setTooltipPosition(updatedPosition);
  }, [isOpen, position, tooltipDimensions, controlLayout, windowDimensions, arrowWidth, arrowOffset, tooltipPosition]);
  const control = children !== undefined ? children : defaultControl;
  const pressableStyles = control === defaultControl ? Platform.select({
    web: {
      outline: 'none'
    }
  }) : undefined;
  const pressableHitSlop = control === defaultControl ? {
    top: 10,
    bottom: 10,
    left: 10,
    right: 10
  } : undefined;
  return /*#__PURE__*/_jsxs(View, {
    style: staticStyles.container,
    ...selectProps(rest),
    children: [/*#__PURE__*/_jsx(Pressable, {
      onPress: toggleIsOpen,
      ref: controlRef,
      onBlur: close,
      style: pressableStyles,
      hitSlop: pressableHitSlop,
      accessibilityLabel: getCopy('a11yText'),
      accessibilityRole: "button",
      children: typeof control === 'function' ? pressableState => control(getPressableState(pressableState), variant) : control
    }), isOpen && /*#__PURE__*/_jsx(Backdrop, {
      onPress: close,
      children: /*#__PURE__*/_jsxs(View, {
        ref: ref,
        style: [staticStyles.tooltip, selectTooltipShadowStyles(themeTokens), // applied separately so that it doesn't cover the arrow
        tooltipPosition && selectTooltipPositionStyles(tooltipPosition), (tooltipPosition === null || (tooltipPosition === null || tooltipPosition === void 0 ? void 0 : tooltipPosition.isNormalized)) && staticStyles.tooltipHidden // visually hide the tooltip until we have a final measurement
        ],
        onLayout: onTooltipLayout,
        accessibilityRole: "alert",
        children: [/*#__PURE__*/_jsx(View, {
          style: [staticStyles.arrow, tooltipPosition && selectArrowStyles(themeTokens, tooltipPosition)]
        }), /*#__PURE__*/_jsx(View, {
          style: selectTooltipStyles(themeTokens),
          children: /*#__PURE__*/_jsx(Text, {
            style: selectTextStyles(themeTokens),
            children: content
          })
        })]
      })
    })]
  });
});
Tooltip.displayName = 'NativeTooltip';
Tooltip.propTypes = { ...selectedSystemPropTypes,
  ...propTypes
};
export default Tooltip;
const staticStyles = StyleSheet.create({
  container: {
    alignItems: 'flex-start'
  },
  tooltip: {
    position: 'absolute',
    maxWidth: 240,
    top: 0,
    left: 0
  },
  tooltipHidden: {
    opacity: 0
  },
  arrow: {
    position: 'absolute'
  }
});